import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { ArrowLeft, RefreshCw, CheckCircle2, AlertTriangle, Package, Truck, Edit3, FileText, Upload, Plus, Replace, Receipt, Calculator, X, ScanBarcode, Loader2, Download, Calendar as CalendarIcon, Layers } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL + '/api';
var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Tiny calendar-popover date cell used in GRN item rows.
// `value` is an ISO "YYYY-MM-DD" string (or ''). Emits the same format on change.
function DateCell(props) {
  var value = props.value || '';
  var onChange = props.onChange;
  var placeholder = props.placeholder || 'Pick date';
  var invalid = !!props.invalid;
  // Parse "YYYY-MM-DD" as LOCAL date (not UTC). `new Date("YYYY-MM-DD")` is
  // parsed as UTC midnight per spec, which shifts by one day in negative-offset
  // timezones. Using the (yyyy, mm-1, dd) constructor sidesteps that entirely.
  function parseISODate(s) {
    if (!s) return null;
    var parts = s.split('-');
    if (parts.length !== 3) return null;
    return new Date(parseInt(parts[0],10), parseInt(parts[1],10)-1, parseInt(parts[2],10));
  }
  var displayValue = '';
  if (value) {
    var parts = value.split('-');
    if (parts.length === 3) displayValue = parts[2] + '/' + parts[1] + '/' + parts[0];
  }
  function handleSelect(date) {
    if (!date) { onChange(''); return; }
    var yy = date.getFullYear();
    var mm = String(date.getMonth() + 1).padStart(2, '0');
    var dd = String(date.getDate()).padStart(2, '0');
    onChange(yy + '-' + mm + '-' + dd);
  }
  // String comparison on ISO dates is bulletproof (lexicographic = chronological)
  // and avoids the Date-object-vs-timezone drift that previously caused
  // same-day or day-after clicks to be spuriously disabled in certain offsets.
  var disabledFn;
  if (props.minDate) {
    disabledFn = function(d) {
      var dd = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
      return dd < props.minDate;
    };
  }
  return React.createElement(Popover, null,
    React.createElement(PopoverTrigger, { asChild: true },
      React.createElement('button', {
        type: 'button',
        className: 'h-8 w-full min-w-[7rem] px-2 flex items-center gap-1.5 text-xs rounded-md border bg-white hover:bg-slate-50 text-left ' + (invalid ? 'border-red-500 bg-red-50 text-red-700' : 'border-slate-300 text-slate-700'),
        'data-testid': props['data-testid']
      },
        React.createElement(CalendarIcon, { className: 'w-3 h-3 shrink-0 text-slate-500' }),
        React.createElement('span', { className: 'truncate flex-1 ' + (displayValue ? '' : 'text-slate-400') }, displayValue || placeholder))),
    React.createElement(PopoverContent, { className: 'w-auto p-0', align: 'start' },
      React.createElement(Calendar, {
        mode: 'single',
        selected: parseISODate(value) || undefined,
        onSelect: handleSelect,
        fromYear: 2015,
        toYear: 2035,
        captionLayout: 'dropdown-buttons',
        disabled: disabledFn,
        initialFocus: true
      })));
}

function GRNCreatePage() {
  var searchParams = useSearchParams()[0];
  var navigate = useNavigate();
  var poId = searchParams.get('poId');
  var isManualMode = searchParams.get('manual') === 'true';
  
  // Core states
  var loadingState = useState(!isManualMode); var loading = loadingState[0]; var setLoading = loadingState[1];
  var submittingState = useState(false); var submitting = submittingState[0]; var setSubmitting = submittingState[1];
  var poDetailsState = useState(null); var poDetails = poDetailsState[0]; var setPoDetails = poDetailsState[1];
  var userAccessState = useState(null); var userAccess = userAccessState[0]; var setUserAccess = userAccessState[1];
  var receivedDateState = useState(new Date().toISOString().split('T')[0]); var receivedDate = receivedDateState[0]; var setReceivedDate = receivedDateState[1];
  var itemsState = useState([]); var items = itemsState[0]; var setItems = itemsState[1];
  var notesState = useState(''); var notes = notesState[0]; var setNotes = notesState[1];
  var disclaimerState = useState(''); var disclaimerRemarks = disclaimerState[0]; var setDisclaimerRemarks = disclaimerState[1];
  var mrpState = useState({}); var mrpUpdates = mrpState[0]; var setMrpUpdates = mrpState[1];
  var shortageState = useState(false); var hasShortage = shortageState[0]; var setHasShortage = shortageState[1];
  var damageState = useState(false); var hasDamagedStock = damageState[0]; var setHasDamagedStock = damageState[1];
  var alternateState = useState(false); var hasAlternate = alternateState[0]; var setHasAlternate = alternateState[1];
  
  // Manual GRN states
  var suppliersState = useState([]); var suppliers = suppliersState[0]; var setSuppliers = suppliersState[1];
  var manualSupplierIdState = useState(''); var manualSupplierId = manualSupplierIdState[0]; var setManualSupplierId = manualSupplierIdState[1];
  var manualSupplierNameState = useState(''); var manualSupplierName = manualSupplierNameState[0]; var setManualSupplierName = manualSupplierNameState[1];
  
  // Invoice states
  var invoiceNumState = useState(''); var invoiceNumber = invoiceNumState[0]; var setInvoiceNumber = invoiceNumState[1];
  var invoiceDateState = useState(''); var invoiceDate = invoiceDateState[0]; var setInvoiceDate = invoiceDateState[1];
  var billValueState = useState(''); var billValue = billValueState[0]; var setBillValue = billValueState[1];
  var invoiceFileState = useState(null); var invoiceFile = invoiceFileState[0]; var setInvoiceFile = invoiceFileState[1];
  var invoiceUrlState = useState(''); var invoiceFileUrl = invoiceUrlState[0]; var setInvoiceFileUrl = invoiceUrlState[1];
  var uploadingState = useState(false); var uploading = uploadingState[0]; var setUploading = uploadingState[1];
  
  // E-Way Bill states
  var ewayFileState = useState(null); var ewayFile = ewayFileState[0]; var setEwayFile = ewayFileState[1];
  var ewayUrlState = useState(''); var ewayFileUrl = ewayUrlState[0]; var setEwayFileUrl = ewayUrlState[1];
  var ewayUploadingState = useState(false); var ewayUploading = ewayUploadingState[0]; var setEwayUploading = ewayUploadingState[1];
  
  // Alternate product modal states
  var altModalState = useState(false); var showAltModal = altModalState[0]; var setShowAltModal = altModalState[1];
  var altItemIdxState = useState(null); var altItemIndex = altItemIdxState[0]; var setAltItemIndex = altItemIdxState[1];
  var productsState = useState([]); var products = productsState[0]; var setProducts = productsState[1];
  var selectedAltState = useState(null); var selectedAltProduct = selectedAltState[0]; var setSelectedAltProduct = selectedAltState[1];
  var altRemarksState = useState(''); var altRemarks = altRemarksState[0]; var setAltRemarks = altRemarksState[1];
  var searchProdState = useState(''); var searchProd = searchProdState[0]; var setSearchProd = searchProdState[1];
  
  // Barcode scanner states
  var scanBarcodeState = useState(''); var scanBarcode = scanBarcodeState[0]; var setScanBarcode = scanBarcodeState[1];
  var scanLoadingState = useState(false); var scanLoading = scanLoadingState[0]; var setScanLoading = scanLoadingState[1];
  var highlightIdxState = useState(null); var highlightIdx = highlightIdxState[0]; var setHighlightIdx = highlightIdxState[1];
  var scannerRef = React.useRef(null);
  var itemRowRefs = React.useRef({});
  
  function fetchSuppliers() {
    var token = localStorage.getItem('token');
    axios.get(API + '/suppliers', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) { setSuppliers(res.data.filter(function(s) { return !s.status || s.status === 'active'; })); })
      .catch(function() { /* Silent — dropdown stays empty until retry. */ });
  }

  useEffect(function() {
    fetchUserAccess();
    fetchProducts();
    if (isManualMode) {
      fetchSuppliers();
      setLoading(false);
    } else if (poId) {
      fetchPODetails();
    } else {
      toast.error('No PO ID provided');
      navigate('/grn-dashboard');
    }
  }, [poId, isManualMode]);

  function fetchPODetails() {
    setLoading(true);
    var token = localStorage.getItem('token');
    axios.get(API + '/grn/po-details/' + poId, { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) {
        setPoDetails(res.data);
        var grnItems = [];
        var respItems = res.data.items;
        for (var i = 0; i < respItems.length; i++) {
          var item = respItems[i];
          if (item.pending_quantity > 0) {
            // B2B price comes directly from the enriched item (from PO data)
            var purchaseRate = item.purchase_rate || 0;
            // Use ?? so a legitimate 0% GST line isn't silently bumped to 18%.
            var gstPct = item.gst_percentage ?? 18;
            var b2bInclGst = purchaseRate * (1 + gstPct / 100);
            grnItems.push({
              product_id: item.product_id,
              product_name: item.product_name,
              product_sku: item.product_sku,
              ordered_quantity: item.pending_quantity,
              received_quantity: 0,
              shortage_quantity: item.pending_quantity,
              damaged_quantity: 0,
              accepted_quantity: 0,
              batch_number: '',
              expiry_date: '',
              mfg_date: '',
              is_alternate: false,
              original_product_id: null,
              original_product_name: null,
              alternate_remarks: '',
              current_mrp: item.mrp || 0,
              new_mrp: item.mrp || 0,
              original_ordered: item.ordered_quantity,
              already_received: item.received_quantity,
              // B2B Pricing - pre-populated from PO
              b2b_price_excl_gst: purchaseRate,
              gst_percentage: gstPct,
              b2b_price_incl_gst: b2bInclGst,
              line_total: 0,
              // Warehouse location (synced to product master + batch on GRN submit)
              rack_no: item.rack_location || '',
              bin_no: item.bin || '',
              // Barcode capture — pre-fill from Master; editable during GRN.
              barcode: item.barcode || item.product_barcode || '',
              barcode_ok: true,
              barcode_error: ''
            });
          }
        }
        setItems(grnItems);
        setLoading(false);
      })
      .catch(function(err) {
        var msg = err.response ? (err.response.data ? err.response.data.detail : 'Error') : 'Error';
        toast.error(msg || 'Failed to fetch PO');
        navigate('/grn-dashboard');
        setLoading(false);
      });
  }

  function fetchUserAccess() {
    var token = localStorage.getItem('token');
    axios.get(API + '/grn/user-access', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) { setUserAccess(res.data); })
      .catch(function() { /* Permissions default-deny on render. */ });
  }

  function fetchProducts() {
    var token = localStorage.getItem('token');
    axios.get(API + '/products', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) { setProducts(res.data); })
      .catch(function() { /* Silent — products list stays empty. */ });
  }

  function handleGrnBarcodeScan() {
    var code = scanBarcode.trim();
    if (!code || scanLoading) return;
    setScanLoading(true);
    var token = localStorage.getItem('token');
    axios.get(API + '/products/barcode/' + encodeURIComponent(code), { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) {
        var product = res.data;
        var matchIdx = -1;
        for (var i = 0; i < items.length; i++) {
          if (items[i].product_id === product.id) { matchIdx = i; break; }
        }
        if (matchIdx >= 0) {
          setHighlightIdx(matchIdx);
          playBeep(true);
          toast.success(product.name + ' found — enter received quantity', { duration: 3000 });
          // Scroll to row and focus received qty input
          setTimeout(function() {
            var row = itemRowRefs.current[matchIdx];
            if (row) {
              row.scrollIntoView({ behavior: 'smooth', block: 'center' });
              var input = row.querySelector('input[data-field="received_quantity"]');
              if (input) { input.focus(); input.select(); }
            }
          }, 200);
          // Clear highlight after 4 seconds
          setTimeout(function() { setHighlightIdx(null); }, 4000);
        } else {
          playBeep(false);
          toast.error('Product not found in this PO', { description: product.name + ' is not part of this Purchase Order' });
        }
        setScanBarcode('');
        setScanLoading(false);
        if (scannerRef.current) scannerRef.current.focus();
      })
      .catch(function() {
        playBeep(false);
        toast.error('Barcode not found', { description: 'No product with barcode: ' + code });
        setScanBarcode('');
        setScanLoading(false);
        if (scannerRef.current) scannerRef.current.focus();
      });
  }

  function handleScanKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); handleGrnBarcodeScan(); }
  }

  // Live barcode-uniqueness check as the buyer edits per-row barcode cells.
  // On blur we call /grn/barcodes/validate and mark the row red if the code
  // is already assigned to a different product. Fully idempotent for the
  // same-SKU case (matches backend semantics).
  function validateBarcodeOnBlur(idx) {
    var it = items[idx]; if (!it) return;
    var bc = (it.barcode || '').trim();
    var token = localStorage.getItem('token');
    if (!bc) {
      var newItems1 = items.slice();
      newItems1[idx] = Object.assign({}, it, { barcode_ok: true, barcode_error: '' });
      setItems(newItems1);
      return;
    }
    axios.post(API + '/grn/barcodes/validate',
      { items: [{ product_id: it.product_id, barcode: bc }] },
      { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) {
        var r = (res.data.results || [])[0] || { ok: true };
        var newItems2 = items.slice();
        newItems2[idx] = Object.assign({}, newItems2[idx], {
          barcode_ok: !!r.ok,
          barcode_error: r.ok ? '' : (r.message || 'Barcode already in use.'),
        });
        setItems(newItems2);
      })
      .catch(function() { /* Silent — backend rejects on real submit anyway. */ });
  }

  function playBeep(success) {
    try {
      var ctx = new (window.AudioContext || window.webkitAudioContext)();
      var osc = ctx.createOscillator(); var gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = success ? 800 : 300; osc.type = 'sine'; gain.gain.value = 0.1;
      osc.start(); osc.stop(ctx.currentTime + (success ? 0.1 : 0.3));
    } catch (e) { /* audio not supported */ }
  }

  function updateItem(index, field, value) {
    var newItems = [];
    for (var i = 0; i < items.length; i++) {
      if (i === index) {
        var item = Object.assign({}, items[i]);
        if (field === 'received_quantity') {
          item.received_quantity = parseFloat(value) || 0;
          item.shortage_quantity = item.received_quantity < item.ordered_quantity ? item.ordered_quantity - item.received_quantity : 0;
        } else if (field === 'damaged_quantity') {
          item.damaged_quantity = parseFloat(value) || 0;
        } else if (field === 'batch_number') {
          item.batch_number = value;
        } else if (field === 'mfg_date') {
          item.mfg_date = value;
        } else if (field === 'expiry_date') {
          item.expiry_date = value;
        } else if (field === 'new_mrp') {
          item.new_mrp = parseFloat(value) || 0;
          if (item.new_mrp !== item.current_mrp) {
            var u = Object.assign({}, mrpUpdates); u[item.product_id] = item.new_mrp; setMrpUpdates(u);
          }
        } else if (field === 'b2b_price_excl_gst') {
          item.b2b_price_excl_gst = parseFloat(value) || 0;
          item.b2b_price_incl_gst = item.b2b_price_excl_gst * (1 + (item.gst_percentage || 0) / 100);
          item.line_total = item.accepted_quantity * item.b2b_price_incl_gst;
        } else if (field === 'b2b_price_incl_gst') {
          // Feb 2026: GRN UI now edits inclusive-GST directly. Back-compute
          // excl-GST so the backend + master sync still get the right value.
          item.b2b_price_incl_gst = parseFloat(value) || 0;
          var gstPctVal = item.gst_percentage || 0;
          item.b2b_price_excl_gst = gstPctVal > 0 ? item.b2b_price_incl_gst / (1 + gstPctVal / 100) : item.b2b_price_incl_gst;
          item.line_total = item.accepted_quantity * item.b2b_price_incl_gst;
        } else if (field === 'gst_percentage') {
          item.gst_percentage = parseFloat(value) || 0;
          item.b2b_price_incl_gst = item.b2b_price_excl_gst * (1 + item.gst_percentage / 100);
          item.line_total = item.accepted_quantity * item.b2b_price_incl_gst;
        } else if (field === 'barcode') {
          item.barcode = String(value || '');
          // Reset validation status while user types — it's re-checked on blur.
          item.barcode_error = '';
          item.barcode_ok = true;
        } else if (field === 'rack_no') {
          item.rack_no = value;
        } else if (field === 'bin_no') {
          item.bin_no = value;
        }
        item.accepted_quantity = Math.max(0, item.received_quantity - item.damaged_quantity);
        item.line_total = item.accepted_quantity * (item.b2b_price_incl_gst || 0);
        newItems.push(item);
      } else {
        newItems.push(items[i]);
      }
    }
    setItems(newItems);
    var anyShort = false, anyDamage = false, anyAlt = false;
    for (var j = 0; j < newItems.length; j++) {
      if (newItems[j].shortage_quantity > 0) anyShort = true;
      if (newItems[j].damaged_quantity > 0) anyDamage = true;
      if (newItems[j].is_alternate) anyAlt = true;
    }
    setHasShortage(anyShort);
    setHasDamagedStock(anyDamage);
    setHasAlternate(anyAlt);
  }

  function handleInvoiceUpload(e) {
    var file = e.target.files[0];
    if (!file) return;
    setInvoiceFile(file);
    setUploading(true);
    var token = localStorage.getItem('token');
    var formData = new FormData();
    formData.append('file', file);
    axios.post(API + '/grn/upload-invoice', formData, { headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' } })
      .then(function(res) { setInvoiceFileUrl(res.data.url); toast.success('Invoice uploaded'); setUploading(false); })
      .catch(function() { toast.error('Failed to upload invoice'); setUploading(false); });
  }

  function handleEwayBillUpload(e) {
    var file = e.target.files[0];
    if (!file) return;
    var validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (validTypes.indexOf(file.type) < 0) {
      toast.error('Invalid file format. Please upload JPG, PNG or PDF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10 MB allowed');
      return;
    }
    setEwayFile(file);
    setEwayUploading(true);
    var token = localStorage.getItem('token');
    var formData = new FormData();
    formData.append('file', file);
    axios.post(API + '/grn/upload-invoice', formData, { headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' } })
      .then(function(res) { setEwayFileUrl(res.data.url); toast.success('E-Way Bill uploaded'); setEwayUploading(false); })
      .catch(function() { toast.error('Failed to upload E-Way Bill'); setEwayUploading(false); });
  }

  function openAltModal(idx) {
    setAltItemIndex(idx);
    setSelectedAltProduct(null);
    setAltRemarks('');
    setSearchProd('');
    setShowAltModal(true);
  }

  function confirmAltProduct() {
    if (!selectedAltProduct) { toast.error('Select a product'); return; }
    if (!altRemarks.trim()) { toast.error('Remarks are mandatory for alternate products'); return; }
    var newItems = [];
    for (var i = 0; i < items.length; i++) {
      if (i === altItemIndex) {
        var item = Object.assign({}, items[i]);
        // Store original product info
        item.original_product_id = item.product_id;
        item.original_product_name = item.product_name;
        // Replace with alternate
        item.product_id = selectedAltProduct.id;
        item.product_name = selectedAltProduct.name;
        item.product_sku = selectedAltProduct.sku;
        item.is_alternate = true;
        item.alternate_remarks = altRemarks;
        item.current_mrp = selectedAltProduct.mrp || 0;
        item.new_mrp = selectedAltProduct.mrp || 0;
        item.b2b_price_excl_gst = selectedAltProduct.b2b_price || 0;
        item.b2b_price_incl_gst = item.b2b_price_excl_gst * (1 + (item.gst_percentage ?? 18) / 100);
        item.line_total = item.accepted_quantity * item.b2b_price_incl_gst;
        newItems.push(item);
      } else {
        newItems.push(items[i]);
      }
    }
    setItems(newItems);
    setHasAlternate(true);
    setShowAltModal(false);
    toast.success('Alternate product mapped');
  }

  var filteredProducts = useMemo(function() {
    if (!searchProd) return products.slice(0, 20);
    var q = searchProd.toLowerCase();
    return products.filter(function(p) {
      return (p.name && p.name.toLowerCase().includes(q)) || (p.sku && p.sku.toLowerCase().includes(q));
    }).slice(0, 20);
  }, [products, searchProd]);

  // Calculate totals
  var totals = useMemo(function() {
    var subtotalExcl = 0, totalGst = 0, grandTotal = 0;
    var totalOrdered = 0, totalReceived = 0, totalAccepted = 0, totalShort = 0, totalDamaged = 0;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      totalOrdered += it.ordered_quantity || 0;
      totalReceived += it.received_quantity || 0;
      totalAccepted += it.accepted_quantity || 0;
      totalShort += it.shortage_quantity || 0;
      totalDamaged += it.damaged_quantity || 0;
      var lineExcl = (it.accepted_quantity || 0) * (it.b2b_price_excl_gst || 0);
      var lineGst = lineExcl * ((it.gst_percentage || 0) / 100);
      subtotalExcl += lineExcl;
      totalGst += lineGst;
      grandTotal += lineExcl + lineGst;
    }
    return { subtotalExcl: subtotalExcl, totalGst: totalGst, grandTotal: grandTotal, totalOrdered: totalOrdered, totalReceived: totalReceived, totalAccepted: totalAccepted, totalShort: totalShort, totalDamaged: totalDamaged, mrpCount: Object.keys(mrpUpdates).length };
  }, [items, mrpUpdates]);

  function handleSubmit() {
    if (items.length === 0) { toast.error('No items to receive'); return; }
    
    // Validate MFG/Expiry dates
    for (var v = 0; v < items.length; v++) {
      if (items[v].mfg_date && items[v].expiry_date && items[v].expiry_date < items[v].mfg_date) {
        toast.error('Expiry date must be after manufacture date for: ' + items[v].product_name);
        return;
      }
    }
    
    // For manual mode, validate supplier
    if (isManualMode) {
      if (!manualSupplierId) { toast.error('Please select a supplier'); return; }
      var invalidItems = items.filter(function(item) { return !item.product_id || item.received_quantity <= 0; });
      if (invalidItems.length > 0) { toast.error('Please select product and enter quantity for all items'); return; }
    } else {
      if (!invoiceNumber.trim()) { toast.error('Supplier Invoice Number is mandatory'); return; }
    }
    
    var needsDisclaimer = hasShortage || hasDamagedStock || hasAlternate;
    if (needsDisclaimer && !disclaimerRemarks.trim()) { toast.error('Disclaimer remarks required for shortage/damaged/alternate items'); return; }
    
    setSubmitting(true);
    var token = localStorage.getItem('token');
    var mrpList = [];
    var mrpKeys = Object.keys(mrpUpdates);
    for (var k = 0; k < mrpKeys.length; k++) {
      mrpList.push({ product_id: mrpKeys[k], new_mrp: mrpUpdates[mrpKeys[k]] });
    }
    // Manual-mode: also include per-row MRP edits made directly in the table
    if (isManualMode) {
      for (var mi = 0; mi < items.length; mi++) {
        var mit = items[mi];
        if (mit.product_id && mit.new_mrp && mit.new_mrp !== mit.current_mrp) {
          // Avoid duplicates if already added via the MRP-update modal
          if (!mrpList.some(function(x) { return x.product_id === mit.product_id; })) {
            mrpList.push({ product_id: mit.product_id, new_mrp: mit.new_mrp });
          }
        }
      }
    }
    var itemsPayload = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      // Validate MRP-split (if enabled) sums to accepted quantity.
      if (it.mrp_splits && it.mrp_splits.length > 0) {
        var splitTotal = it.mrp_splits.reduce(function(s, x) { return s + (parseFloat(x.quantity) || 0); }, 0);
        if (Math.abs(splitTotal - (it.accepted_quantity || 0)) > 0.01) {
          toast.error((it.product_name || 'Row ' + (i + 1)) + ' — MRP split qty ' + splitTotal + ' must equal Accepted ' + it.accepted_quantity);
          setSubmitting(false);
          return;
        }
      }
      itemsPayload.push({
        product_id: it.product_id, product_name: it.product_name, product_sku: it.product_sku, ordered_quantity: it.ordered_quantity || it.received_quantity,
        received_quantity: it.received_quantity, shortage_quantity: it.shortage_quantity || 0, damaged_quantity: it.damaged_quantity || 0,
        accepted_quantity: it.accepted_quantity, batch_number: it.batch_number || null, expiry_date: it.expiry_date || null, mfg_date: it.mfg_date || null,
        is_alternate: it.is_alternate || false, original_product_id: it.original_product_id, original_product_name: it.original_product_name,
        alternate_remarks: it.alternate_remarks || null,
        b2b_price_excl_gst: it.b2b_price_excl_gst, gst_percentage: it.gst_percentage,
        b2b_price_incl_gst: it.b2b_price_incl_gst, line_total: it.line_total,
        rack_no: (it.rack_no || '').trim() || null,
        bin_no: (it.bin_no || '').trim() || null,
        barcode: (it.barcode || '').trim() || null,
        mrp_splits: (it.mrp_splits && it.mrp_splits.length > 0) ? it.mrp_splits.map(function(s) { return { mrp: parseFloat(s.mrp) || 0, quantity: parseFloat(s.quantity) || 0 }; }) : null
      });
    }
    
    if (isManualMode) {
      // Manual GRN API
      var manualPayload = {
        supplier_id: manualSupplierId, supplier_name: manualSupplierName, received_date: receivedDate, items: itemsPayload, notes: notes || null,
        has_shortage: hasShortage, has_alternate_stock: hasAlternate, has_damaged_stock: hasDamagedStock,
        disclaimer_remarks: disclaimerRemarks || null, mrp_updates: mrpList.length > 0 ? mrpList : null,
        invoice_number: invoiceNumber || null, invoice_date: invoiceDate || null, invoice_file_url: invoiceFileUrl || null,
        bill_value: billValue !== '' && !isNaN(parseFloat(billValue)) ? parseFloat(billValue) : null,
        eway_bill_file_url: ewayFileUrl || null,
        is_manual: true
      };
      axios.post(API + '/grn/manual', manualPayload, { headers: { Authorization: 'Bearer ' + token } })
        .then(function(res) {
          var applied = res.data && res.data.mrp_updates_count;
          if (applied && applied > 0) {
            toast.success('Manual GRN ' + (res.data.grn_number || '') + ' created · ' + applied + ' MRP update' + (applied === 1 ? '' : 's') + ' applied');
          } else {
            toast.success('Manual GRN created successfully!');
          }
          navigate('/grn-dashboard');
          setSubmitting(false);
        })
        .catch(function(err) {
          setSubmitting(false);
          var resp = err.response || {};
          var d = resp.data || {};
          if (resp.status === 409 && d.detail && d.detail.conflicts) {
            var msgs = d.detail.conflicts.map(function(c) { return '• ' + c.product_name + ' → ' + c.attempted_barcode + ' (already used by ' + c.conflict_with_product_name + ')'; }).join('\n');
            toast.error('Barcode collision — GRN not saved', { description: msgs, duration: 8000 });
            return;
          }
          var m = d.detail || 'Error';
          toast.error(typeof m === 'string' ? m : JSON.stringify(m));
        });
    } else {
      // PO-based GRN API
      var payload = {
        purchase_order_id: poId, received_date: receivedDate, items: itemsPayload, notes: notes || null,
        has_shortage: hasShortage, has_alternate_stock: hasAlternate, has_damaged_stock: hasDamagedStock,
        disclaimer_remarks: disclaimerRemarks || null, mrp_updates: mrpList.length > 0 ? mrpList : null,
        invoice_number: invoiceNumber, invoice_date: invoiceDate || null, invoice_file_url: invoiceFileUrl || null,
        bill_value: billValue !== '' && !isNaN(parseFloat(billValue)) ? parseFloat(billValue) : null,
        eway_bill_file_url: ewayFileUrl || null,
        subtotal_excl_gst: totals.subtotalExcl, total_gst_amount: totals.totalGst, grand_total_incl_gst: totals.grandTotal
      };
      axios.post(API + '/grn/with-mrp-update', payload, { headers: { Authorization: 'Bearer ' + token } })
        .then(function(res) {
          var applied = res.data && res.data.mrp_updates_count;
          if (applied && applied > 0) {
            toast.success('GRN ' + (res.data.grn_number || '') + ' created · ' + applied + ' MRP update' + (applied === 1 ? '' : 's') + ' applied');
          } else {
            toast.success('GRN created successfully!');
          }
          navigate('/grn-dashboard');
          setSubmitting(false);
        })
        .catch(function(err) {
          setSubmitting(false);
          var resp = err.response || {};
          var d = resp.data || {};
          if (resp.status === 409 && d.detail && d.detail.conflicts) {
            var msgs = d.detail.conflicts.map(function(c) { return '• ' + c.product_name + ' → ' + c.attempted_barcode + ' (already used by ' + c.conflict_with_product_name + ')'; }).join('\n');
            toast.error('Barcode collision — GRN not saved', { description: msgs, duration: 8000 });
            return;
          }
          var m = d.detail || 'Error';
          toast.error(typeof m === 'string' ? m : JSON.stringify(m));
        });
    }
  }
  
  // Add manual item
  function addManualItem() {
    setItems(items.concat({
      product_id: '', product_name: '', product_sku: '', ordered_quantity: 0,
      received_quantity: 0, shortage_quantity: 0, damaged_quantity: 0, accepted_quantity: 0,
      batch_number: '', expiry_date: '', is_alternate: false, original_product_id: null, original_product_name: null,
      alternate_remarks: '', b2b_price_excl_gst: 0, gst_percentage: 0, b2b_price_incl_gst: 0, line_total: 0, current_mrp: 0,
      new_mrp: 0,
      rack_no: '', bin_no: '',
      barcode: '', barcode_ok: true, barcode_error: ''
    }));
  }
  
  // Update manual item product selection
  function updateManualItemProduct(index, productId) {
    var product = products.find(function(p) { return p.id === productId; });
    if (!product) return;
    // Duplicate-product guard: prevent adding the same SKU twice in one Manual GRN
    var dupIdx = items.findIndex(function(it, i) { return i !== index && it.product_id === productId; });
    if (dupIdx !== -1) {
      toast.error(product.name + ' is already on row ' + (dupIdx + 1), {
        description: 'Edit that row instead — a product can only appear once per GRN.'
      });
      return;
    }
    var newItems = items.slice();
    var gst = product.gst_percentage || 0;
    var b2bExcl = product.inward_b2b_price || product.purchase_price || 0;
    var b2bIncl = Number((b2bExcl * (1 + gst / 100)).toFixed(2));
    newItems[index] = Object.assign({}, newItems[index], {
      product_id: productId,
      product_name: product.name,
      product_sku: product.sku || '',
      current_mrp: product.mrp || 0,
      new_mrp: product.mrp || 0,
      b2b_price_excl_gst: b2bExcl,
      gst_percentage: gst,
      b2b_price_incl_gst: b2bIncl,
      line_total: Number(((newItems[index].accepted_quantity || 0) * b2bIncl).toFixed(2)),
      rack_no: newItems[index].rack_no || product.rack_location || '',
      bin_no: newItems[index].bin_no || product.bin || '',
      barcode: newItems[index].barcode || product.barcode || '',
      barcode_ok: true,
      barcode_error: ''
    });
    setItems(newItems);
  }

  // Manual GRN barcode scanner: existing row → highlight & +1 qty; new product → add prefilled row
  function handleManualBarcodeScan() {
    var code = scanBarcode.trim();
    if (!code || scanLoading) return;
    setScanLoading(true);
    var token = localStorage.getItem('token');
    axios.get(API + '/products/barcode/' + encodeURIComponent(code), { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) {
        var product = res.data;
        var existingIdx = items.findIndex(function(it) { return it.product_id === product.id; });
        if (existingIdx !== -1) {
          // Already on the list — increment received_quantity by 1 and highlight
          var newItems = items.slice();
          var row = Object.assign({}, newItems[existingIdx]);
          row.received_quantity = (parseFloat(row.received_quantity) || 0) + 1;
          row.ordered_quantity = row.received_quantity;
          row.accepted_quantity = Math.max(0, row.received_quantity - (row.damaged_quantity || 0));
          row.line_total = Number((row.accepted_quantity * (row.b2b_price_incl_gst || 0)).toFixed(2));
          newItems[existingIdx] = row;
          setItems(newItems);
          setHighlightIdx(existingIdx);
          setTimeout(function() { setHighlightIdx(null); }, 1500);
          playBeep(true);
          toast.success(product.name + ' +1 (qty ' + row.received_quantity + ')');
        } else {
          // New product — add a prefilled row
          var gst = product.gst_percentage || 0;
          var b2bExcl = product.inward_b2b_price || product.inward_price_excl_gst || product.purchase_price || 0;
          var b2bIncl = product.inward_price_incl_gst || Number((b2bExcl * (1 + gst / 100)).toFixed(2));
          var accepted = 1;
          var newRow = {
            product_id: product.id,
            product_name: product.name,
            product_sku: product.sku || product.product_id || '',
            ordered_quantity: 1,
            received_quantity: 1,
            shortage_quantity: 0,
            damaged_quantity: 0,
            accepted_quantity: accepted,
            batch_number: '',
            expiry_date: '',
            mfg_date: '',
            is_alternate: false,
            original_product_id: null,
            original_product_name: null,
            alternate_remarks: '',
            b2b_price_excl_gst: b2bExcl,
            gst_percentage: gst,
            b2b_price_incl_gst: b2bIncl,
            line_total: Number((accepted * b2bIncl).toFixed(2)),
            current_mrp: product.mrp || 0,
            new_mrp: product.mrp || 0,
            rack_no: product.rack_location || '',
            bin_no: product.bin || '',
            barcode: product.barcode || code,
            barcode_ok: true,
            barcode_error: ''
          };
          var updated = items.concat(newRow);
          setItems(updated);
          setHighlightIdx(updated.length - 1);
          setTimeout(function() { setHighlightIdx(null); }, 1500);
          playBeep(true);
          toast.success(product.name + ' added to GRN');
        }
        setScanBarcode('');
        setScanLoading(false);
        if (scannerRef.current) scannerRef.current.focus();
      })
      .catch(function() {
        playBeep(false);
        toast.error('Barcode not found', { description: 'No product with barcode: ' + code });
        setScanBarcode('');
        setScanLoading(false);
        if (scannerRef.current) scannerRef.current.focus();
      });
  }

  function handleManualScanKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); handleManualBarcodeScan(); }
  }
  
  // Update manual item quantity
  function updateManualItemQty(index, field, value) {
    var newItems = items.slice();
    var item = Object.assign({}, newItems[index]);
    if (field === 'received_quantity') {
      item.received_quantity = parseFloat(value) || 0;
      item.ordered_quantity = item.received_quantity;
      item.accepted_quantity = Math.max(0, item.received_quantity - (item.damaged_quantity || 0));
    } else if (field === 'damaged_quantity') {
      item.damaged_quantity = parseFloat(value) || 0;
      item.accepted_quantity = Math.max(0, item.received_quantity - item.damaged_quantity);
    } else if (field === 'expiry_date') {
      item.expiry_date = value;
    } else if (field === 'new_mrp') {
      item.new_mrp = parseFloat(value) || 0;
    } else if (field === 'b2b_price_excl_gst') {
      item.b2b_price_excl_gst = parseFloat(value) || 0;
      item.b2b_price_incl_gst = Number((item.b2b_price_excl_gst * (1 + (item.gst_percentage || 0) / 100)).toFixed(2));
    } else if (field === 'b2b_price_incl_gst') {
      // Feb 2026 — Manual GRN also edits Incl-GST directly; back-compute excl.
      item.b2b_price_incl_gst = parseFloat(value) || 0;
      var gstPctManual = item.gst_percentage || 0;
      item.b2b_price_excl_gst = gstPctManual > 0 ? Number((item.b2b_price_incl_gst / (1 + gstPctManual / 100)).toFixed(2)) : item.b2b_price_incl_gst;
    } else if (field === 'gst_percentage') {
      item.gst_percentage = parseFloat(value) || 0;
      item.b2b_price_incl_gst = Number(((item.b2b_price_excl_gst || 0) * (1 + item.gst_percentage / 100)).toFixed(2));
    } else if (field === 'barcode') {
      item.barcode = String(value || '');
      item.barcode_ok = true;
      item.barcode_error = '';
    } else if (field === 'mfg_date') {
      item.mfg_date = value;
    } else if (field === 'rack_no') {
      item.rack_no = value;
    } else if (field === 'bin_no') {
      item.bin_no = value;
    }
    // Recompute line_total on any change that affects accepted_qty or price
    item.line_total = Number(((item.accepted_quantity || 0) * (item.b2b_price_incl_gst || 0)).toFixed(2));
    newItems[index] = item;
    setItems(newItems);
    setHasDamagedStock(newItems.some(function(i) { return i.damaged_quantity > 0; }));
  }

  // --- Multi-MRP split helpers (Manual GRN) -----------------------------------
  function toggleMrpSplit(index) {
    var newItems = items.slice();
    var item = Object.assign({}, newItems[index]);
    if (item.mrp_splits && item.mrp_splits.length > 0) {
      // Turn OFF: clear splits, restore single MRP flow
      item.mrp_splits = null;
    } else {
      // Turn ON: seed with one split carrying the entire accepted qty at the current MRP
      var firstMrp = item.new_mrp || item.current_mrp || 0;
      item.mrp_splits = [{ mrp: firstMrp, quantity: item.accepted_quantity || 0 }];
    }
    newItems[index] = item;
    setItems(newItems);
  }

  function addMrpSplitRow(index) {
    var newItems = items.slice();
    var item = Object.assign({}, newItems[index]);
    var splits = (item.mrp_splits || []).slice();
    splits.push({ mrp: 0, quantity: 0 });
    item.mrp_splits = splits;
    newItems[index] = item;
    setItems(newItems);
  }

  function updateMrpSplit(index, splitIdx, field, value) {
    var newItems = items.slice();
    var item = Object.assign({}, newItems[index]);
    var splits = (item.mrp_splits || []).slice();
    splits[splitIdx] = Object.assign({}, splits[splitIdx], { [field]: parseFloat(value) || 0 });
    item.mrp_splits = splits;
    newItems[index] = item;
    setItems(newItems);
  }

  function removeMrpSplit(index, splitIdx) {
    var newItems = items.slice();
    var item = Object.assign({}, newItems[index]);
    var splits = (item.mrp_splits || []).filter(function(_, i) { return i !== splitIdx; });
    item.mrp_splits = splits.length > 0 ? splits : null;
    newItems[index] = item;
    setItems(newItems);
  }

  function splitSumQty(item) {
    if (!item.mrp_splits) return 0;
    return item.mrp_splits.reduce(function(s, x) { return s + (parseFloat(x.quantity) || 0); }, 0);
  }
  
  // Remove manual item
  function removeManualItem(index) {
    setItems(items.filter(function(_, i) { return i !== index; }));
  }

  if (loading) {
    return React.createElement('div', { className: 'flex items-center justify-center h-64' },
      React.createElement(RefreshCw, { className: 'w-6 h-6 animate-spin text-gray-400 mr-2' }),
      React.createElement('span', { className: 'text-gray-600' }, 'Loading PO details...')
    );
  }

  if (!isManualMode && !poDetails) {
    return React.createElement('div', { className: 'text-center py-12' },
      React.createElement(Package, { className: 'w-12 h-12 mx-auto mb-3 text-gray-300' }),
      React.createElement('p', { className: 'text-gray-500' }, 'PO not found'),
      React.createElement(Button, { onClick: function() { navigate('/grn-dashboard'); }, className: 'mt-4' }, 'Back')
    );
  }
  
  // For Manual Mode, render the manual GRN form
  if (isManualMode) {
    var canUpdateMrp = userAccess ? userAccess.can_update_mrp : false;
    return React.createElement('div', { className: 'space-y-6', 'data-testid': 'manual-grn-page' },
      // Header
      React.createElement('div', { className: 'flex items-center justify-between' },
        React.createElement('div', { className: 'flex items-center gap-4' },
          React.createElement(Button, { variant: 'outline', size: 'sm', onClick: function() { navigate('/grn-dashboard'); }, className: 'gap-1', 'data-testid': 'back-btn' },
            React.createElement(ArrowLeft, { className: 'w-4 h-4' }), 'Back'),
          React.createElement('div', null,
            React.createElement('h1', { className: 'text-2xl font-heading font-bold text-primary' }, 'Create Manual GRN'),
            React.createElement('p', { className: 'text-slate-600 text-sm' }, 'Stock receipt without Purchase Order'))),
        React.createElement(Badge, { className: 'bg-orange-100 text-orange-800' }, 'MANUAL ENTRY')),
      
      // Supplier & Date Card
      React.createElement(Card, { className: 'border-orange-200 bg-orange-50/50' },
        React.createElement(CardHeader, { className: 'pb-3' },
          React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' },
            React.createElement(Truck, { className: 'w-5 h-5 text-orange-600' }), 'Manual GRN Details')),
        React.createElement(CardContent, null,
          React.createElement('div', { className: 'grid grid-cols-2 md:grid-cols-3 gap-4' },
            React.createElement('div', null,
              React.createElement(Label, { className: 'text-sm font-medium' }, 'Supplier *'),
              React.createElement(Select, { value: manualSupplierId, onValueChange: function(val) { 
                setManualSupplierId(val); 
                var sup = suppliers.find(function(s) { return s.id === val; });
                setManualSupplierName(sup ? sup.name : '');
              } },
                React.createElement(SelectTrigger, { 'data-testid': 'manual-supplier-select' },
                  React.createElement(SelectValue, { placeholder: 'Select supplier...' })),
                React.createElement(SelectContent, null,
                  suppliers.map(function(s) { return React.createElement(SelectItem, { key: s.id, value: s.id }, s.name); })))),
            React.createElement('div', null,
              React.createElement(Label, { className: 'text-sm font-medium' }, 'Received Date'),
              React.createElement(Input, { type: 'date', value: receivedDate, onChange: function(e) { setReceivedDate(e.target.value); }, 'data-testid': 'received-date' })),
            React.createElement('div', null,
              React.createElement(Label, { className: 'text-sm font-medium' }, 'Invoice Number (Optional)'),
              React.createElement(Input, { value: invoiceNumber, onChange: function(e) { setInvoiceNumber(e.target.value); }, placeholder: 'Invoice #' }))))),
      
      // Items Card
      React.createElement(Card, null,
        React.createElement(CardHeader, { className: 'pb-3 flex flex-row items-center justify-between' },
          React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' },
            React.createElement(Package, { className: 'w-5 h-5 text-blue-600' }), 'Items to Receive'),
          React.createElement(Button, { variant: 'outline', size: 'sm', onClick: addManualItem, className: 'gap-1 border-orange-300 text-orange-700 hover:bg-orange-50', 'data-testid': 'add-manual-item-btn' },
            React.createElement(Plus, { className: 'w-4 h-4' }), 'Add Item')),
        React.createElement(CardContent, null,
          // Barcode scanner (Manual GRN) — scan to add or increment product
          React.createElement('div', { className: 'p-3 mb-4 bg-slate-50 rounded-lg border border-slate-200', 'data-testid': 'manual-grn-barcode-scanner' },
            React.createElement('div', { className: 'flex items-center gap-2 mb-2' },
              React.createElement(ScanBarcode, { className: 'w-4 h-4 text-emerald-600' }),
              React.createElement('span', { className: 'text-sm font-medium text-slate-700' }, 'Barcode Scanner')),
            React.createElement('div', { className: 'flex gap-2' },
              React.createElement('div', { className: 'relative flex-1' },
                React.createElement(ScanBarcode, { className: 'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' }),
                React.createElement(Input, { ref: scannerRef, type: 'text', value: scanBarcode, onChange: function(e) { setScanBarcode(e.target.value); }, onKeyDown: handleManualScanKeyDown, placeholder: 'Scan or type barcode, then press Enter...', className: 'pl-10 font-mono', disabled: scanLoading, 'data-testid': 'manual-barcode-input' })),
              React.createElement(Button, { type: 'button', onClick: handleManualBarcodeScan, disabled: scanLoading || !scanBarcode.trim(), className: 'bg-emerald-600 hover:bg-emerald-700 gap-2', 'data-testid': 'manual-barcode-find-btn' },
                scanLoading ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(ScanBarcode, { className: 'w-4 h-4' }), 'Add')),
            React.createElement('p', { className: 'text-xs text-slate-500 mt-1.5' }, 'Scan a barcode to auto-add the product (pre-fills MRP, B2B & GST). If already on the list, quantity increments by 1.')),
          items.length === 0 
            ? React.createElement('div', { className: 'text-center py-8 border rounded-lg bg-gray-50' },
                React.createElement(Package, { className: 'w-10 h-10 mx-auto mb-2 text-gray-300' }),
                React.createElement('p', { className: 'text-gray-500' }, 'No items added yet'),
                React.createElement('p', { className: 'text-sm text-gray-400' }, 'Click "Add Item" to start'))
            : React.createElement('div', { className: 'border rounded-lg overflow-x-auto' },
                React.createElement('table', { className: 'w-full text-sm' },
                  React.createElement('thead', { className: 'bg-orange-50' },
                    React.createElement('tr', null,
                      React.createElement('th', { className: 'px-3 py-2 text-left font-semibold' }, 'Product *'),
                      React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-36 bg-emerald-50', title: 'Product barcode — edits update Master Catalogue.' }, 'Barcode'),
                      React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-24' }, 'Quantity *'),
                      React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-20' }, 'Damaged'),
                      React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-20 bg-green-100' }, 'Accepted'),
                      React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-24' }, 'MRP'),
                      React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-28', title: 'Inward B2B Rate (Inclusive of GST)' }, 'Rate (Incl GST)'),
                      React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-20' }, 'GST %'),
                      React.createElement('th', { className: 'px-2 py-2 text-right font-semibold w-28 bg-blue-50' }, 'Line Total'),
                      React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-24' }, 'Expiry'),
                      React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-24 bg-violet-50' }, 'Rack'),
                      React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-24 bg-violet-50' }, 'Bin'),
                      React.createElement('th', { className: 'px-2 py-2 w-10' }, ''))),
                  React.createElement('tbody', { className: 'divide-y' },
                    items.map(function(item, idx) {
                      var hasSplit = item.mrp_splits && item.mrp_splits.length > 0;
                      var splitSum = hasSplit ? splitSumQty(item) : 0;
                      var splitDelta = hasSplit ? Math.round(((item.accepted_quantity || 0) - splitSum) * 100) / 100 : 0;
                      var mainRow = React.createElement('tr', { key: 'main-' + idx, className: 'hover:bg-orange-50/50' + (highlightIdx === idx ? ' ring-2 ring-emerald-400 bg-emerald-50' : '') },
                        React.createElement('td', { className: 'px-3 py-2' },
                          React.createElement(Select, { value: item.product_id, onValueChange: function(val) { updateManualItemProduct(idx, val); } },
                            React.createElement(SelectTrigger, { className: 'h-8', 'data-testid': 'manual-product-' + idx },
                              React.createElement(SelectValue, { placeholder: 'Select product...' })),
                            React.createElement(SelectContent, null,
                              products.map(function(p) { return React.createElement(SelectItem, { key: p.id, value: p.id }, p.name + (p.barcode ? ' (' + p.barcode + ')' : '')); })))),
                        React.createElement('td', { className: 'px-2 py-2 bg-emerald-50/50' },
                          React.createElement(Input, {
                            type: 'text',
                            value: item.barcode || '',
                            onChange: function(e) { updateManualItemQty(idx, 'barcode', e.target.value); },
                            onBlur: function() { validateBarcodeOnBlur(idx); },
                            className: 'h-8 font-mono text-xs w-32 mx-auto ' + (item.barcode_ok === false ? 'border-red-500 bg-red-50 text-red-800' : ''),
                            placeholder: 'Barcode',
                            'data-testid': 'manual-barcode-cell-' + idx
                          }),
                          item.barcode_ok === false && React.createElement('p', { className: 'text-[10px] text-red-600 mt-0.5', 'data-testid': 'manual-barcode-error-' + idx }, item.barcode_error || 'Duplicate')),
                        React.createElement('td', { className: 'px-2 py-2' },
                          React.createElement(Input, { type: 'number', min: '0', step: '0.01', value: item.received_quantity, onChange: function(e) { updateManualItemQty(idx, 'received_quantity', e.target.value); }, className: 'h-8 text-center w-full font-medium', 'data-testid': 'manual-qty-' + idx })),
                        React.createElement('td', { className: 'px-2 py-2' },
                          React.createElement(Input, { type: 'number', min: '0', step: '1', value: item.damaged_quantity || 0, onChange: function(e) { updateManualItemQty(idx, 'damaged_quantity', e.target.value); }, className: 'h-8 text-center' })),
                        React.createElement('td', { className: 'px-2 py-2 text-center bg-green-100/50' },
                          React.createElement('span', { className: 'font-bold text-green-700' }, item.accepted_quantity)),
                        React.createElement('td', { className: 'px-2 py-2' },
                          hasSplit
                            ? React.createElement('span', { className: 'text-xs text-amber-700 italic' }, 'Multi-MRP')
                            : React.createElement(Input, { type: 'number', min: '0', step: '0.01', value: item.new_mrp || 0, onChange: function(e) { updateManualItemQty(idx, 'new_mrp', e.target.value); }, className: 'h-8 text-right', 'data-testid': 'manual-mrp-' + idx })),
                        React.createElement('td', { className: 'px-2 py-2' },
                          React.createElement(Input, { type: 'number', min: '0', step: '0.01', value: item.b2b_price_incl_gst || 0, onChange: function(e) { updateManualItemQty(idx, 'b2b_price_incl_gst', e.target.value); }, className: 'h-8 text-right', 'data-testid': 'manual-rate-incl-' + idx })),
                        React.createElement('td', { className: 'px-2 py-2' },
                          React.createElement(Input, { type: 'number', min: '0', step: '0.01', value: item.gst_percentage || 0, onChange: function(e) { updateManualItemQty(idx, 'gst_percentage', e.target.value); }, className: 'h-8 text-center' })),
                        React.createElement('td', { className: 'px-2 py-2 text-right bg-blue-50/50' },
                          React.createElement('span', { className: 'font-bold text-blue-700', 'data-testid': 'manual-line-total-' + idx }, '\u20B9' + (item.line_total || 0).toFixed(2))),
                        React.createElement('td', { className: 'px-2 py-2' },
                          React.createElement(DateCell, { value: item.expiry_date || '', onChange: function(v) { updateManualItemQty(idx, 'expiry_date', v); }, placeholder: 'Expiry', 'data-testid': 'manual-expiry-' + idx })),
                        React.createElement('td', { className: 'px-2 py-2 bg-violet-50/40' },
                          React.createElement(Input, { type: 'text', value: item.rack_no || '', onChange: function(e) { updateManualItemQty(idx, 'rack_no', e.target.value); }, className: 'h-8 text-center', placeholder: 'Rack', 'data-testid': 'manual-rack-' + idx })),
                        React.createElement('td', { className: 'px-2 py-2 bg-violet-50/40' },
                          React.createElement(Input, { type: 'text', value: item.bin_no || '', onChange: function(e) { updateManualItemQty(idx, 'bin_no', e.target.value); }, className: 'h-8 text-center', placeholder: 'Bin', 'data-testid': 'manual-bin-' + idx })),
                        React.createElement('td', { className: 'px-2 py-2 text-center' },
                          React.createElement('div', { className: 'flex items-center justify-center gap-1' },
                            React.createElement(Button, {
                              size: 'sm', variant: 'ghost', onClick: function() { toggleMrpSplit(idx); },
                              title: hasSplit ? 'Disable MRP split' : 'Split across multiple MRPs',
                              className: 'h-7 w-7 p-0 ' + (hasSplit ? 'text-amber-700 bg-amber-100 hover:bg-amber-200' : 'text-slate-500 hover:text-amber-600 hover:bg-amber-50'),
                              'data-testid': 'manual-split-toggle-' + idx
                            }, React.createElement(Layers, { size: 14 })),
                            React.createElement(Button, { size: 'sm', variant: 'ghost', onClick: function() { removeManualItem(idx); }, className: 'h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50' },
                              React.createElement(X, { size: 14 })))));
                      if (!hasSplit) return mainRow;
                      // Split sub-row: mini inline table of { MRP, Qty } pairs with live remainder.
                      var splitRow = React.createElement('tr', { key: 'split-' + idx, className: 'bg-amber-50/40 border-t border-amber-200' },
                        React.createElement('td', { colSpan: 13, className: 'px-4 py-3' },
                          React.createElement('div', { className: 'flex items-start gap-4' },
                            React.createElement('div', { className: 'text-xs font-semibold text-amber-800 uppercase pt-1' }, 'MRP split →'),
                            React.createElement('div', { className: 'flex-1 space-y-2', 'data-testid': 'manual-split-block-' + idx },
                              item.mrp_splits.map(function(sp, si) {
                                return React.createElement('div', { key: si, className: 'flex items-center gap-2', 'data-testid': 'manual-split-row-' + idx + '-' + si },
                                  React.createElement('span', { className: 'text-xs text-slate-600 w-8' }, '#' + (si + 1)),
                                  React.createElement('label', { className: 'text-xs text-slate-600' }, 'MRP ₹'),
                                  React.createElement(Input, { type: 'number', min: '0', step: '0.01', value: sp.mrp, onChange: function(e) { updateMrpSplit(idx, si, 'mrp', e.target.value); }, className: 'h-7 w-28 text-right', 'data-testid': 'manual-split-mrp-' + idx + '-' + si }),
                                  React.createElement('label', { className: 'text-xs text-slate-600 ml-2' }, 'Qty'),
                                  React.createElement(Input, { type: 'number', min: '0', step: '0.01', value: sp.quantity, onChange: function(e) { updateMrpSplit(idx, si, 'quantity', e.target.value); }, className: 'h-7 w-24 text-center', 'data-testid': 'manual-split-qty-' + idx + '-' + si }),
                                  React.createElement(Button, { size: 'sm', variant: 'ghost', onClick: function() { removeMrpSplit(idx, si); }, className: 'h-7 w-7 p-0 text-red-500 hover:bg-red-50' },
                                    React.createElement(X, { size: 12 })));
                              }),
                              React.createElement('div', { className: 'flex items-center gap-3 pt-1' },
                                React.createElement(Button, { size: 'sm', variant: 'outline', onClick: function() { addMrpSplitRow(idx); }, className: 'h-7 text-xs gap-1 border-amber-300 text-amber-800 hover:bg-amber-100', 'data-testid': 'manual-split-add-' + idx },
                                  React.createElement(Plus, { size: 12 }), 'Add MRP'),
                                React.createElement('span', { className: 'text-xs ' + (Math.abs(splitDelta) < 0.01 ? 'text-emerald-700' : 'text-red-600'), 'data-testid': 'manual-split-remainder-' + idx },
                                  'Allocated: ' + splitSum + ' / ' + (item.accepted_quantity || 0) +
                                  (Math.abs(splitDelta) < 0.01 ? ' ✓' : ' (' + (splitDelta > 0 ? 'short ' + splitDelta : 'over ' + Math.abs(splitDelta)) + ')')))))));
                      return [mainRow, splitRow];
                    })),
                  // Grand-total footer
                  React.createElement('tfoot', { className: 'bg-blue-50/60 border-t-2 border-blue-200' },
                    React.createElement('tr', null,
                      React.createElement('td', { colSpan: 8, className: 'px-3 py-2 text-right font-semibold text-blue-900' }, 'Grand Total (Incl. GST)'),
                      React.createElement('td', { className: 'px-2 py-2 text-right font-bold text-blue-900', 'data-testid': 'manual-grand-total' },
                        '\u20B9' + items.reduce(function(s, i) { return s + (i.line_total || 0); }, 0).toFixed(2)),
                      React.createElement('td', { colSpan: 4 }))))))),
      
      // Notes
      React.createElement(Card, null,
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement(Label, { className: 'text-sm font-medium' }, 'Notes (Optional)'),
          React.createElement(Textarea, { value: notes, onChange: function(e) { setNotes(e.target.value); }, placeholder: 'Additional notes...', className: 'mt-1' }))),
      
      // Submit Button
      React.createElement('div', { className: 'flex justify-end gap-3' },
        React.createElement(Button, { variant: 'outline', onClick: function() { navigate('/grn-dashboard'); } }, 'Cancel'),
        React.createElement(Button, { onClick: handleSubmit, disabled: submitting || items.length === 0 || !manualSupplierId, className: 'gap-2 bg-orange-600 hover:bg-orange-700', 'data-testid': 'submit-manual-grn' },
          submitting ? React.createElement(RefreshCw, { className: 'w-4 h-4 animate-spin' }) : React.createElement(CheckCircle2, { className: 'w-4 h-4' }),
          submitting ? 'Creating...' : 'Create Manual GRN'))
    );
  }

  var po = poDetails.po;
  var canUpdateMrp = userAccess ? userAccess.can_update_mrp : false;
  // MRP Variance Alert — lines where the received (GRN) MRP differs from the PO MRP.
  var mrpVariantLines = (items || []).filter(function(it) {
    if (it.mrp_splits && it.mrp_splits.length > 0) return false;
    var a = parseFloat(it.current_mrp) || 0; var b = parseFloat(it.new_mrp) || 0;
    return a > 0 && b > 0 && Math.abs(a - b) > 0.009;
  });

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'grn-create-page' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('div', { className: 'flex items-center gap-4' },
        React.createElement(Button, { variant: 'outline', size: 'sm', onClick: function() { navigate('/grn-dashboard'); }, className: 'gap-1', 'data-testid': 'back-btn' },
          React.createElement(ArrowLeft, { className: 'w-4 h-4' }), 'Back'),
        React.createElement('div', null,
          React.createElement('h1', { className: 'text-2xl font-heading font-bold text-primary' }, 'Create GRN'),
          React.createElement('p', { className: 'text-slate-600 text-sm' }, 'Goods Receipt Note for ' + po.po_number))),
      React.createElement(Badge, { className: po.status === 'partially_received' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800' },
        po.status ? po.status.toUpperCase().replace('_', ' ') : 'PENDING')),
    
    // PO Info Card
    React.createElement(Card, { className: 'border-blue-200 bg-blue-50/50' },
      React.createElement(CardHeader, { className: 'pb-3' },
        React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' },
          React.createElement(FileText, { className: 'w-5 h-5 text-blue-600' }), 'Purchase Order Details')),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'grid grid-cols-2 md:grid-cols-4 gap-4' },
          React.createElement('div', null, React.createElement('p', { className: 'text-xs text-gray-500 uppercase' }, 'PO Number'), React.createElement('p', { className: 'font-mono font-semibold text-blue-600' }, po.po_number)),
          React.createElement('div', null, React.createElement('p', { className: 'text-xs text-gray-500 uppercase' }, 'Supplier'), React.createElement('p', { className: 'font-medium' }, po.supplier_name)),
          po.retailer_name && React.createElement('div', null, React.createElement('p', { className: 'text-xs text-gray-500 uppercase' }, 'For Retailer'), React.createElement('p', { className: 'font-medium' }, po.retailer_name)),
          React.createElement('div', null, React.createElement('p', { className: 'text-xs text-gray-500 uppercase' }, 'PO Date'), React.createElement('p', { className: 'font-medium' }, po.po_date || 'N/A'))))),
    
    // Summary Stats
    React.createElement('div', { className: 'grid grid-cols-3 gap-4' },
      React.createElement(Card, { className: 'bg-gray-50' }, React.createElement(CardContent, { className: 'p-4 text-center' }, React.createElement('p', { className: 'text-xs text-gray-500 uppercase' }, 'Total Ordered'), React.createElement('p', { className: 'text-2xl font-bold' }, poDetails.total_ordered))),
      React.createElement(Card, { className: 'bg-green-50' }, React.createElement(CardContent, { className: 'p-4 text-center' }, React.createElement('p', { className: 'text-xs text-green-600 uppercase' }, 'Already Received'), React.createElement('p', { className: 'text-2xl font-bold text-green-700' }, poDetails.total_received))),
      React.createElement(Card, { className: 'bg-orange-50' }, React.createElement(CardContent, { className: 'p-4 text-center' }, React.createElement('p', { className: 'text-xs text-orange-600 uppercase' }, 'Pending to Receive'), React.createElement('p', { className: 'text-2xl font-bold text-orange-700' }, poDetails.total_pending)))),
    
    // Invoice Capture Section
    React.createElement(Card, { className: 'border-purple-200 bg-purple-50/30' },
      React.createElement(CardHeader, { className: 'pb-3' },
        React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' },
          React.createElement(Receipt, { className: 'w-5 h-5 text-purple-600' }), 'Supplier Invoice Details',
          React.createElement(Badge, { className: 'bg-red-100 text-red-700 ml-2' }, 'Required'))),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-5 gap-4' },
          React.createElement('div', null,
            React.createElement(Label, { className: 'text-purple-800 font-medium' }, 'Invoice Number *'),
            React.createElement(Input, { value: invoiceNumber, onChange: function(e) { setInvoiceNumber(e.target.value); }, placeholder: 'Enter supplier invoice no.', className: 'mt-1 bg-white', 'data-testid': 'invoice-number-input' })),
          React.createElement('div', null,
            React.createElement(Label, { className: 'text-purple-800' }, 'Invoice Date'),
            React.createElement(Input, { type: 'date', value: invoiceDate, onChange: function(e) { setInvoiceDate(e.target.value); }, className: 'mt-1 bg-white' })),
          React.createElement('div', null,
            React.createElement(Label, { className: 'text-purple-800' }, 'Bill Value (₹) ', React.createElement('span', { className: 'text-xs text-gray-400 font-normal' }, '(Optional)')),
            React.createElement(Input, { type: 'number', step: '0.01', min: 0, value: billValue, onChange: function(e) { setBillValue(e.target.value); }, placeholder: 'Supplier invoice total', className: 'mt-1 bg-white', 'data-testid': 'bill-value-input' })),
          React.createElement('div', null,
            React.createElement(Label, { className: 'text-purple-800' }, 'Upload Invoice (PDF/Image)'),
            React.createElement('div', { className: 'mt-1 flex items-center gap-2' },
              React.createElement('label', { className: 'flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 rounded-md cursor-pointer hover:bg-purple-50 transition-colors' },
                React.createElement(Upload, { className: 'w-4 h-4 text-purple-600' }),
                React.createElement('span', { className: 'text-sm' }, uploading ? 'Uploading...' : (invoiceFile ? invoiceFile.name : 'Choose File')),
                React.createElement('input', { type: 'file', accept: '.pdf,.jpg,.jpeg,.png', onChange: handleInvoiceUpload, className: 'hidden' })),
              invoiceFileUrl && React.createElement(Badge, { className: 'bg-green-100 text-green-700' }, 'Uploaded'))),
          React.createElement('div', null,
            React.createElement(Label, { className: 'text-blue-700' }, 'E-Way Bill ', React.createElement('span', { className: 'text-xs text-gray-400 font-normal' }, '(Optional)')),
            React.createElement('div', { className: 'mt-1 flex items-center gap-2' },
              React.createElement('label', { className: 'flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 rounded-md cursor-pointer hover:bg-blue-50 transition-colors', 'data-testid': 'eway-bill-upload-label' },
                React.createElement(Upload, { className: 'w-4 h-4 text-blue-600' }),
                React.createElement('span', { className: 'text-sm' }, ewayUploading ? 'Uploading...' : (ewayFile ? ewayFile.name : 'Choose File')),
                React.createElement('input', { type: 'file', accept: '.pdf,.jpg,.jpeg,.png', onChange: handleEwayBillUpload, className: 'hidden', 'data-testid': 'eway-bill-upload-input' })),
              ewayFileUrl && React.createElement('div', { className: 'flex items-center gap-2' },
                React.createElement(Badge, { className: 'bg-green-100 text-green-700' }, 'Uploaded'),
                React.createElement('a', { href: BACKEND_URL + ewayFileUrl, target: '_blank', rel: 'noreferrer', className: 'text-xs text-blue-600 hover:underline' }, 'View'))))))),
    
    // GRN Items Form
    React.createElement(Card, null,
      React.createElement(CardHeader, { className: 'pb-3' },
        React.createElement(CardTitle, { className: 'text-lg flex items-center justify-between' },
          React.createElement('span', { className: 'flex items-center gap-2' }, React.createElement(Truck, { className: 'w-5 h-5 text-green-600' }), 'Enter Received Quantities & Pricing'),
          React.createElement('div', { className: 'flex items-center gap-2' },
            React.createElement(Label, { className: 'text-sm text-gray-500' }, 'Received Date:'),
            React.createElement(Input, { type: 'date', value: receivedDate, onChange: function(e) { setReceivedDate(e.target.value); }, className: 'w-36 h-8' })))),
      React.createElement(CardContent, null,
        canUpdateMrp && React.createElement('div', { className: 'p-3 mb-4 bg-green-50 rounded-lg border border-green-200' },
          React.createElement('div', { className: 'flex items-center gap-2 text-green-800' },
            React.createElement(Edit3, { className: 'w-4 h-4' }),
            React.createElement('span', { className: 'font-medium text-sm' }, 'MRP Correction Enabled'))),
        
        // Items Table with B2B Pricing
        React.createElement('div', { className: 'p-3 mb-4 bg-slate-50 rounded-lg border border-slate-200', 'data-testid': 'grn-barcode-scanner' },
          React.createElement('div', { className: 'flex items-center gap-2 mb-2' },
            React.createElement(ScanBarcode, { className: 'w-4 h-4 text-emerald-600' }),
            React.createElement('span', { className: 'text-sm font-medium text-slate-700' }, 'Barcode Scanner'),
            React.createElement(Badge, { variant: 'outline', className: 'text-xs border-emerald-300 text-emerald-700' }, 'Scan to find')),
          React.createElement('div', { className: 'flex gap-2' },
            React.createElement('div', { className: 'relative flex-1' },
              React.createElement(ScanBarcode, { className: 'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' }),
              React.createElement(Input, { ref: scannerRef, type: 'text', value: scanBarcode, onChange: function(e) { setScanBarcode(e.target.value); }, onKeyDown: handleScanKeyDown, placeholder: 'Scan barcode to find product in PO...', className: 'pl-10 font-mono', disabled: scanLoading, 'data-testid': 'grn-barcode-input' })),
            React.createElement(Button, { type: 'button', onClick: handleGrnBarcodeScan, disabled: scanLoading || !scanBarcode.trim(), className: 'bg-emerald-600 hover:bg-emerald-700 gap-2', 'data-testid': 'grn-barcode-find-btn' },
              scanLoading ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(ScanBarcode, { className: 'w-4 h-4' }), 'Find')),
          React.createElement('p', { className: 'text-xs text-slate-500 mt-1.5' }, 'Scan product barcode to locate it in the PO items list and enter received quantity.')),

        // Feb 2026: Excl-GST columns fully removed from GRN UI per user spec
        // (backend still receives them via back-computation from Incl-GST rate).

        React.createElement('div', { className: 'border rounded-lg overflow-x-auto' },
          React.createElement('table', { className: 'w-full text-sm min-w-[1750px]' },
            React.createElement('thead', { className: 'bg-gray-100' },
              React.createElement('tr', null,
                React.createElement('th', { className: 'px-2 py-2 text-left font-semibold w-44' }, 'Product'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-36 bg-emerald-50', title: 'Product barcode. Pre-filled from Master Catalogue; edit to update the master.' }, 'Barcode'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-16' }, 'PO Qty'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-20' }, 'Received'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-14' }, 'Short'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-14' }, 'Dmgd'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-14' }, 'Accpt'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-16 bg-indigo-50' }, 'GST%'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-24 bg-indigo-50', title: 'Inward B2B Rate inclusive of GST' }, 'Rate (Incl GST)'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-24 bg-amber-50' }, 'Line Total'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-16 bg-green-50', title: 'MRP from the Purchase Order line (frozen reference)' }, 'PO MRP'),
                canUpdateMrp && React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-20 bg-green-50', title: 'Actual MRP received — becomes the Batch MRP and updates the Product Master MRP' }, 'GRN MRP (Actual)'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-28 bg-cyan-50' }, 'MFG Date'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-28 bg-cyan-50' }, 'Expiry Date'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-24 bg-violet-50' }, 'Rack'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-24 bg-violet-50' }, 'Bin'),
                React.createElement('th', { className: 'px-2 py-2 text-center font-semibold w-16' }, 'Action'))),
            React.createElement('tbody', { className: 'divide-y' },
              items.map(function(item, idx) {
                var hasSplit = item.mrp_splits && item.mrp_splits.length > 0;
                var splitSum = hasSplit ? splitSumQty(item) : 0;
                var splitDelta = hasSplit ? Math.round(((item.accepted_quantity || 0) - splitSum) * 100) / 100 : 0;
                // 17 base cells + 1 optional Update-MRP cell = up to 18 columns.
                var visibleCols = 17 + (canUpdateMrp ? 1 : 0);
                var mainRow = React.createElement('tr', { key: 'main-' + idx, ref: function(el) { itemRowRefs.current[idx] = el; }, className: 'hover:bg-gray-50 ' + (item.is_alternate ? 'bg-orange-50' : '') + (highlightIdx === idx ? ' ring-2 ring-emerald-400 bg-emerald-50' : '') },
                  React.createElement('td', { className: 'px-2 py-2' },
                    React.createElement('p', { className: 'font-medium text-sm' }, item.product_name),
                    React.createElement('p', { className: 'text-xs text-gray-400' }, item.product_sku),
                    item.is_alternate && React.createElement('div', { className: 'mt-1' },
                      React.createElement(Badge, { className: 'bg-orange-100 text-orange-800 text-xs' }, 'ALT: ' + item.original_product_name))),
                  React.createElement('td', { className: 'px-2 py-2 bg-emerald-50/50' },
                    React.createElement(Input, {
                      type: 'text',
                      value: item.barcode || '',
                      onChange: function(e) { updateItem(idx, 'barcode', e.target.value); },
                      onBlur: function() { validateBarcodeOnBlur(idx); },
                      className: 'h-8 font-mono text-xs w-32 mx-auto ' + (item.barcode_ok === false ? 'border-red-500 bg-red-50 text-red-800' : ''),
                      placeholder: 'Barcode',
                      'data-testid': 'grn-barcode-cell-' + idx
                    }),
                    item.barcode_ok === false && React.createElement('p', { className: 'text-[10px] text-red-600 mt-0.5', 'data-testid': 'grn-barcode-error-' + idx }, item.barcode_error || 'Duplicate')),
                  React.createElement('td', { className: 'px-2 py-2 text-center font-semibold text-orange-600' }, item.ordered_quantity),
                  React.createElement('td', { className: 'px-2 py-2 text-center' },
                    React.createElement(Input, { type: 'number', min: 0, value: item.received_quantity, onChange: function(e) { updateItem(idx, 'received_quantity', e.target.value); }, className: 'h-8 text-center w-16 mx-auto', 'data-field': 'received_quantity' })),
                  React.createElement('td', { className: 'px-2 py-2 text-center ' + (item.shortage_quantity > 0 ? 'text-yellow-600 font-semibold' : 'text-gray-400') }, item.shortage_quantity),
                  React.createElement('td', { className: 'px-2 py-2 text-center' },
                    React.createElement(Input, { type: 'number', min: 0, value: item.damaged_quantity, onChange: function(e) { updateItem(idx, 'damaged_quantity', e.target.value); }, className: 'h-8 text-center w-14 mx-auto' })),
                  React.createElement('td', { className: 'px-2 py-2 text-center font-semibold text-green-600' }, item.accepted_quantity),
                  React.createElement('td', { className: 'px-2 py-2 text-center bg-indigo-50' },
                    React.createElement(Select, { value: String(item.gst_percentage), onValueChange: function(v) { updateItem(idx, 'gst_percentage', v); } },
                      React.createElement(SelectTrigger, { className: 'h-8 w-16 mx-auto' }, React.createElement(SelectValue, null)),
                      React.createElement(SelectContent, null,
                        React.createElement(SelectItem, { value: '0' }, '0%'),
                        React.createElement(SelectItem, { value: '5' }, '5%'),
                        React.createElement(SelectItem, { value: '12' }, '12%'),
                        React.createElement(SelectItem, { value: '18' }, '18%'),
                        React.createElement(SelectItem, { value: '28' }, '28%'),
                        React.createElement(SelectItem, { value: '40' }, '40%')))),
                  React.createElement('td', { className: 'px-2 py-2 text-center bg-indigo-50' },
                    React.createElement(Input, { type: 'number', min: 0, step: '0.01', value: item.b2b_price_incl_gst || 0, onChange: function(e) { updateItem(idx, 'b2b_price_incl_gst', e.target.value); }, className: 'h-8 text-center w-24 mx-auto font-medium', 'data-testid': 'grn-rate-incl-' + idx })),
                  React.createElement('td', { className: 'px-2 py-2 text-center bg-amber-50 font-semibold text-amber-700' }, '\u20B9' + (item.line_total || 0).toFixed(2)),
                  React.createElement('td', { className: 'px-2 py-2 text-center bg-green-50 font-medium text-green-700' },
                    hasSplit
                      ? React.createElement('span', { className: 'text-xs text-amber-700 italic' }, 'Multi-MRP')
                      : '₹' + (item.current_mrp || 0).toFixed(2)),
                  canUpdateMrp && React.createElement('td', { className: 'px-2 py-2 text-center bg-green-50' },
                    hasSplit
                      ? React.createElement('span', { className: 'text-xs text-slate-400 italic' }, '—')
                      : (function() {
                          var poRef = parseFloat(item.current_mrp) || 0;
                          var grnVal = parseFloat(item.new_mrp) || 0;
                          var varAmt = (poRef > 0 && grnVal > 0) ? grnVal - poRef : 0;
                          var varPct = (poRef > 0 && Math.abs(varAmt) > 0.009) ? (varAmt / poRef) * 100 : 0;
                          var severe = Math.abs(varPct) > 10;
                          var borderCls = Math.abs(varAmt) > 0.009
                            ? (severe ? 'border-red-500 bg-red-50' : 'border-amber-500 bg-amber-50')
                            : '';
                          return React.createElement(React.Fragment, null,
                            React.createElement(Input, { type: 'number', min: 0, step: '0.01', value: item.new_mrp, onChange: function(e) { updateItem(idx, 'new_mrp', e.target.value); }, className: 'h-8 text-center w-20 mx-auto ' + borderCls, 'data-testid': 'grn-mrp-input-' + idx }),
                            Math.abs(varAmt) > 0.009 && React.createElement('p', {
                              className: 'text-[10px] font-semibold mt-0.5 ' + (severe ? 'text-red-600' : 'text-amber-700'),
                              title: 'Received MRP differs from PO MRP ₹' + poRef.toFixed(2),
                              'data-testid': 'mrp-variance-badge-' + idx,
                            }, (varAmt > 0 ? '▲ +' : '▼ ') + varAmt.toFixed(2) + ' (' + (varPct > 0 ? '+' : '') + varPct.toFixed(1) + '%)'));
                        })()),
                  React.createElement('td', { className: 'px-2 py-2 text-center bg-cyan-50' },
                    React.createElement(DateCell, { value: item.mfg_date || '', onChange: function(v) { updateItem(idx, 'mfg_date', v); }, placeholder: 'MFG date', 'data-testid': 'grn-mfg-' + idx })),
                  React.createElement('td', { className: 'px-2 py-2 text-center bg-cyan-50' },
                    React.createElement(DateCell, { value: item.expiry_date || '', onChange: function(v) { updateItem(idx, 'expiry_date', v); }, placeholder: 'Expiry', minDate: item.mfg_date || null, invalid: !!(item.mfg_date && item.expiry_date && item.expiry_date < item.mfg_date), 'data-testid': 'grn-expiry-' + idx }),
                    item.mfg_date && item.expiry_date && item.expiry_date < item.mfg_date && React.createElement('p', { className: 'text-xs text-red-500 mt-0.5' }, 'Must be after MFG')),
                  React.createElement('td', { className: 'px-2 py-2 bg-violet-50/40' },
                    React.createElement(Input, { type: 'text', value: item.rack_no || '', onChange: function(e) { updateItem(idx, 'rack_no', e.target.value); }, className: 'h-8 text-center w-20 mx-auto', placeholder: 'Rack', 'data-testid': 'grn-rack-' + idx })),
                  React.createElement('td', { className: 'px-2 py-2 bg-violet-50/40' },
                    React.createElement(Input, { type: 'text', value: item.bin_no || '', onChange: function(e) { updateItem(idx, 'bin_no', e.target.value); }, className: 'h-8 text-center w-20 mx-auto', placeholder: 'Bin', 'data-testid': 'grn-bin-' + idx })),
                  React.createElement('td', { className: 'px-2 py-2 text-center' },
                    React.createElement('div', { className: 'flex items-center justify-center gap-1' },
                      React.createElement(Button, {
                        variant: 'ghost', size: 'sm', onClick: function() { toggleMrpSplit(idx); },
                        title: hasSplit ? 'Disable MRP split' : 'Split across multiple MRPs',
                        className: 'h-7 w-7 p-0 ' + (hasSplit ? 'text-amber-700 bg-amber-100 hover:bg-amber-200' : 'text-slate-500 hover:text-amber-600 hover:bg-amber-50'),
                        'data-testid': 'po-split-toggle-' + idx
                      }, React.createElement(Layers, { size: 14 })),
                      !item.is_alternate && React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { openAltModal(idx); }, className: 'h-7 w-7 p-0 text-orange-600 hover:text-orange-800', title: 'Replace with Alternate Product' },
                        React.createElement(Replace, { className: 'w-4 h-4' })))));
                if (!hasSplit) return mainRow;
                var splitRow = React.createElement('tr', { key: 'split-' + idx, className: 'bg-amber-50/40 border-t border-amber-200' },
                  React.createElement('td', { colSpan: visibleCols, className: 'px-4 py-3' },
                    React.createElement('div', { className: 'flex items-start gap-4' },
                      React.createElement('div', { className: 'text-xs font-semibold text-amber-800 uppercase pt-1' }, 'MRP split →'),
                      React.createElement('div', { className: 'flex-1 space-y-2', 'data-testid': 'po-split-block-' + idx },
                        item.mrp_splits.map(function(sp, si) {
                          return React.createElement('div', { key: si, className: 'flex items-center gap-2', 'data-testid': 'po-split-row-' + idx + '-' + si },
                            React.createElement('span', { className: 'text-xs text-slate-600 w-8' }, '#' + (si + 1)),
                            React.createElement('label', { className: 'text-xs text-slate-600' }, 'MRP ₹'),
                            React.createElement(Input, { type: 'number', min: '0', step: '0.01', value: sp.mrp, onChange: function(e) { updateMrpSplit(idx, si, 'mrp', e.target.value); }, className: 'h-7 w-28 text-right', 'data-testid': 'po-split-mrp-' + idx + '-' + si }),
                            React.createElement('label', { className: 'text-xs text-slate-600 ml-2' }, 'Qty'),
                            React.createElement(Input, { type: 'number', min: '0', step: '0.01', value: sp.quantity, onChange: function(e) { updateMrpSplit(idx, si, 'quantity', e.target.value); }, className: 'h-7 w-24 text-center', 'data-testid': 'po-split-qty-' + idx + '-' + si }),
                            React.createElement(Button, { size: 'sm', variant: 'ghost', onClick: function() { removeMrpSplit(idx, si); }, className: 'h-7 w-7 p-0 text-red-500 hover:bg-red-50' },
                              React.createElement(X, { size: 12 })));
                        }),
                        React.createElement('div', { className: 'flex items-center gap-3 pt-1' },
                          React.createElement(Button, { size: 'sm', variant: 'outline', onClick: function() { addMrpSplitRow(idx); }, className: 'h-7 text-xs gap-1 border-amber-300 text-amber-800 hover:bg-amber-100', 'data-testid': 'po-split-add-' + idx },
                            React.createElement(Plus, { size: 12 }), 'Add MRP'),
                          React.createElement('span', { className: 'text-xs ' + (Math.abs(splitDelta) < 0.01 ? 'text-emerald-700' : 'text-red-600'), 'data-testid': 'po-split-remainder-' + idx },
                            'Allocated: ' + splitSum + ' / ' + (item.accepted_quantity || 0) +
                            (Math.abs(splitDelta) < 0.01 ? ' ✓' : ' (' + (splitDelta > 0 ? 'short ' + splitDelta : 'over ' + Math.abs(splitDelta)) + ')')))))));
                return [mainRow, splitRow];
              })))))),
    
    // MRP Variance Alert — warns the GRN team before submit (non-blocking)
    mrpVariantLines.length > 0 && React.createElement(Card, { className: 'border-red-300 bg-red-50', 'data-testid': 'mrp-variance-alert' },
      React.createElement(CardContent, { className: 'pt-5 pb-4' },
        React.createElement('div', { className: 'flex items-center gap-2 text-red-800 mb-2' },
          React.createElement(AlertTriangle, { size: 18 }),
          React.createElement('span', { className: 'font-semibold' }, 'MRP Variance Alert — ' + mrpVariantLines.length + ' product(s) received with an MRP different from the PO'),
          React.createElement(Badge, { className: 'bg-red-100 text-red-800 ml-1' }, 'Review before submit')),
        React.createElement('p', { className: 'text-xs text-red-700 mb-2' }, 'On submit, each GRN MRP becomes that batch\u2019s MRP and updates the Product Master MRP. The PO MRP stays frozen for audit.'),
        React.createElement('div', { className: 'space-y-1' },
          mrpVariantLines.map(function(it, vi) {
            var a = parseFloat(it.current_mrp) || 0; var b = parseFloat(it.new_mrp) || 0;
            var pct = a > 0 ? ((b - a) / a) * 100 : 0;
            return React.createElement('div', { key: vi, className: 'text-sm bg-white border border-red-200 rounded px-3 py-1.5 flex items-center justify-between', 'data-testid': 'mrp-variance-line-' + vi },
              React.createElement('span', { className: 'font-medium text-slate-700' }, it.product_name || it.product_sku || ''),
              React.createElement('span', { className: 'font-mono text-xs' },
                'PO MRP ₹' + a.toFixed(2) + ' → GRN MRP ₹' + b.toFixed(2) + ' ',
                React.createElement('span', { className: 'font-semibold ' + (Math.abs(pct) > 10 ? 'text-red-600' : 'text-amber-700') },
                  '(' + (pct > 0 ? '+' : '') + pct.toFixed(1) + '%)')));
          })))),

    // PO Value Summary Card
    React.createElement(Card, { className: 'border-amber-200 bg-amber-50/50' },
      React.createElement(CardHeader, { className: 'pb-3' },
        React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' },
          React.createElement(Calculator, { className: 'w-5 h-5 text-amber-600' }), 'PO Value Summary')),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'grid grid-cols-2 gap-6' },
          React.createElement('div', { className: 'text-center p-4 bg-white rounded-lg border' },
            React.createElement('p', { className: 'text-sm text-gray-500' }, 'Total GST Amount'),
            React.createElement('p', { className: 'text-2xl font-bold text-blue-600' }, '₹' + totals.totalGst.toFixed(2))),
          React.createElement('div', { className: 'text-center p-4 bg-amber-100 rounded-lg border border-amber-300' },
            React.createElement('p', { className: 'text-sm text-amber-700 font-medium' }, 'Grand Total (Incl GST)'),
            React.createElement('p', { className: 'text-3xl font-bold text-amber-800' }, '₹' + totals.grandTotal.toFixed(2)))))),
    
    // Disclaimer Section
    (hasShortage || hasDamagedStock || hasAlternate) && React.createElement(Card, { className: 'border-yellow-300 bg-yellow-50' },
      React.createElement(CardContent, { className: 'pt-6' },
        React.createElement('div', { className: 'flex items-center gap-2 text-yellow-800 mb-3' },
          React.createElement(AlertTriangle, { size: 18 }),
          React.createElement('span', { className: 'font-semibold' }, 'Disclaimer Required'),
          hasAlternate && React.createElement(Badge, { className: 'bg-orange-100 text-orange-800 ml-2' }, 'Alternate Products')),
        React.createElement(Label, { className: 'text-yellow-800' }, 'Remarks / Disclaimer Notes *'),
        React.createElement(Textarea, { value: disclaimerRemarks, onChange: function(e) { setDisclaimerRemarks(e.target.value); }, placeholder: 'Explain the reason for shortage, damaged items, or alternate product replacement...', className: 'mt-1 bg-white', rows: 3, 'data-testid': 'disclaimer-input' }))),
    
    // Additional Notes
    React.createElement(Card, null,
      React.createElement(CardContent, { className: 'pt-6' },
        React.createElement(Label, null, 'Additional Notes (Optional)'),
        React.createElement(Textarea, { value: notes, onChange: function(e) { setNotes(e.target.value); }, placeholder: 'Any additional remarks...', className: 'mt-1', rows: 2 }))),
    
    // Submit Buttons
    React.createElement('div', { className: 'flex justify-end gap-3' },
      React.createElement(Button, { variant: 'outline', onClick: function() { navigate('/grn-dashboard'); } }, 'Cancel'),
      React.createElement(Button, { onClick: handleSubmit, disabled: submitting, className: 'gap-2', 'data-testid': 'submit-grn-btn' },
        React.createElement(CheckCircle2, { className: 'w-4 h-4' }),
        submitting ? 'Creating GRN...' : 'Confirm & Create GRN')),
    
    // Alternate Product Modal
    React.createElement(Dialog, { open: showAltModal, onOpenChange: setShowAltModal },
      React.createElement(DialogContent, { className: 'max-w-lg' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
            React.createElement(Replace, { className: 'w-5 h-5 text-orange-600' }), 'Replace with Alternate Product')),
        React.createElement('div', { className: 'space-y-4 py-4' },
          altItemIndex !== null && items[altItemIndex] && React.createElement('div', { className: 'p-3 bg-gray-50 rounded-lg' },
            React.createElement('p', { className: 'text-sm text-gray-500' }, 'Original PO Product:'),
            React.createElement('p', { className: 'font-medium' }, items[altItemIndex].product_name),
            React.createElement('p', { className: 'text-xs text-gray-400' }, items[altItemIndex].product_sku)),
          React.createElement('div', null,
            React.createElement(Label, null, 'Search Alternate Product'),
            React.createElement(Input, { value: searchProd, onChange: function(e) { setSearchProd(e.target.value); }, placeholder: 'Search by name or SKU...', className: 'mt-1' })),
          React.createElement('div', { className: 'max-h-48 overflow-y-auto border rounded-lg' },
            filteredProducts.map(function(p) {
              var isSelected = selectedAltProduct && selectedAltProduct.id === p.id;
              return React.createElement('div', { key: p.id, onClick: function() { setSelectedAltProduct(p); }, className: 'p-2 border-b cursor-pointer hover:bg-blue-50 ' + (isSelected ? 'bg-blue-100' : '') },
                React.createElement('p', { className: 'font-medium text-sm' }, p.name),
                React.createElement('p', { className: 'text-xs text-gray-400' }, p.sku, ' | MRP: ₹', p.mrp || 0));
            })),
          selectedAltProduct && React.createElement('div', { className: 'p-3 bg-green-50 rounded-lg border border-green-200' },
            React.createElement('p', { className: 'text-sm text-green-700' }, 'Selected: ', React.createElement('strong', null, selectedAltProduct.name))),
          React.createElement('div', null,
            React.createElement(Label, { className: 'text-orange-700' }, 'Reason / Remarks *'),
            React.createElement(Textarea, { value: altRemarks, onChange: function(e) { setAltRemarks(e.target.value); }, placeholder: 'Mandatory: Explain why alternate product is being received...', className: 'mt-1', rows: 2 }))),
        React.createElement(DialogFooter, null,
          React.createElement(Button, { variant: 'outline', onClick: function() { setShowAltModal(false); } }, 'Cancel'),
          React.createElement(Button, { onClick: confirmAltProduct, className: 'bg-orange-600 hover:bg-orange-700' }, 'Confirm Replacement'))))
  );
}

export default GRNCreatePage;
